export const LORE_ENTRIES = [
  {
    id: "grandTournament",
    title: "The Grand Tournament",
    summary: "Why champions gather in the arena, and who keeps the peace.",
    blocks: [
      "For about two hundred years, disputes between signatory realms have been settled by champions in the arena at the Seventh Field.",
      "The Grand Tournament is run by an old pact of rulers, guilds and temples that settles disputes in the arena instead of through war, and profits from the peace.",
      "Champions come for glory, coin, and the Boon, which is one claim the signatories must honor for the winner.",
    ],
  },
  {
    id: "magic",
    title: "Magic",
    summary: "The arts of body, hand and mind, and the price of dark power.",
    blocks: [
      "Strength is the art of the body, Dexterity the hand, Wisdom the mind, and Neutral is plain grit anyone can use.",
      "Fire, cold, and lightning can be channeled through any of them.",
      "Dark magic belongs to no one and is always paid for in blood or life.",
    ],
  },
  {
    id: "lifeInPeacetime",
    title: "Life in Peacetime",
    summary: "Roads, records, money, and work after two centuries of arena peace.",
    blocks: [
      {
        type: "list",
        items: [
          "**Roads.** With fewer soldiers on them, roads are patrolled by hired escorts. Long-distance carriers travel in convoys, and escort companies charge by the cart. Toll bridges are common.",
          "**Secondhand arms.** Old armor and weapons are sold by weight at fairs, then relined and fitted by a local armorer. Most working people who own a helm bought it this way (*Iron Helm*, *Chain Mail*).",
          "**Old forts.** Many are now granaries, sheep pens, or tenements. A few have been turned into escort companies' depots.",
          "**Records.** Because events are dated by Tournament, parish registers, guild ledgers, and loan contracts use the same numbering. A document is often dated “the second spring after the thirty-ninth.” Documents from before the first Tournament use regional reckonings that are hard to reconcile.",
          "**Military.** Standing armies are small, and most realms keep only garrisons and road patrols. Old forts have been sold or let to farmers.",
          "**Trade in arms.** Smiths make fewer, better pieces. Arms and armor are resold, relined and handed down, and a helm can have several owners.",
          "**Money.** Realms and towns borrow from guild banks to pay filing fees and hire champions. Interest is paid in coin, grain, ore, or labor.",
          "**Work.** Discharged soldiers and their children took up escort work, gate work, labor at the Field, or banditry.",
          "**Calendar.** Many people date events by Tournament, e.g. “the winter after the thirty-sixth.”",
        ],
      },
    ],
  },
  {
    id: "seventhField",
    title: "The Seventh Field",
    summary:
      "The arena city built on an old battlefield, and the people who keep it running.",
    blocks: [
      {
        type: "list",
        items: [
          "**Setting.** A river plain where six battles were fought before the first Tournament. The arena is the “Seventh Field,” and the city that grew around it is called the Field.",
          "**Daily life.** About sixty thousand people live there, more in Tournament years. Most work serves the arena: gate porters, sand rakers, cooks, laundries, tanners for the leather trade, farriers, clerks, menders. Housing is rented by the season. Rents double from spring of a Tournament year.",
          "**Food.** Maudry rye and barley, Oss smoked eel, river carp, and cheap mutton from drovers. Street stalls near the arena sell eel pie and barley porridge. Fresh fruit is expensive.",
          {
            text: "**Districts:**",
            items: [
              "**The Stands.** The arena and its stone seating. It holds the Crowns bench, the Ledgers bench and the Altars bench, in that order from the sand. The public stands are above them.",
              "**Kettle Street.** Wager clerks, pawnbrokers, cobblers, sword cleaners, rented rooms, and the bleeders' rooms.",
              "**The Gate.** Weapons inspection, fee collection, and the equipment store where arena staff sign gear in and out.",
              "**Menders' Row.** The Menders' Hall and the apothecaries who supply it.",
              "**The Wash.** Laundries and tanneries downstream. It smells, and rents are cheapest there.",
              "**The docket office.** Clerks who receive petitions and schedule the lesser lists. The waiting benches outside are full most mornings.",
            ],
          },
          "**Law.** The Field has its own watch and magistrates. Theft of arena equipment is tried by the city.",
          {
            text: "**Three eras:**",
            ordered: true,
            items: [
              "Grassed earthworks from the six battles in the eastern suburbs.",
              "Early Tournament-era stone stands and the gatehouse.",
              "Recent brick counting-houses on Kettle Street, and wooden tenements in the Wash.",
            ],
          },
        ],
      },
      { type: "heading", text: "More Districts" },
      {
        type: "list",
        items: [
          "**The Drove Yards:** pens and slaughterhouses on the western road, where Kell drovers sell mutton. Tallow chandlers and bone-boilers work behind them.",
          "**The Quay:** river landings for Oss eel boats and Maudry grain barges, with bonded warehouses.",
          "**The Earthworks:** the eastern suburbs built into and around the old battle banks, mostly cottages and market gardens. Plows still turn up arrowheads and buckles, and people sell them at the Quay on market days.",
        ],
      },
      { type: "heading", text: "Work" },
      {
        type: "list",
        items: [
          "**Sand rakers** work in crews of eight. They rake and water the arena floor before and after each day's bouts and sieve it once a season.",
          "**Gate porters** carry equipment between the store and the arena.",
          "**Sword cleaners** on Kettle Street clean, oil, and rewrap weapons.",
        ],
      },
      "**Daily rhythm.** Bout days start with the sand crews at first light. Stalls open when the Gate opens. Wager clerks close their books when the first bout is called and reopen after the last. On days without bouts, the Field runs like any market town.",
      "**Friction.** Rents set by the season push arena laborers into the Wash every Tournament year and back out afterward. Landlords on Kettle Street keep lists of reliable tenants and charge newcomers a deposit of one season's rent.",
    ],
  },
  {
    id: "oss",
    title: "The Oss",
    summary:
      "A shifting river delta of eel weirs, pearl beds, and disputed mud flats.",
    blocks: [
      {
        type: "list",
        items: [
          "**Setting.** A wide river delta whose main channel shifts every few years. There are around forty towns on raised ground, connected by ferries and causeways.",
          "**Daily life.** People fish, cut peat, tend eel weirs, and dive for river pearls. Houses stand on posts. Every town has a dike board, a council that assigns work on the dikes. Children learn to pole a boat young. Few Oss children are schooled.",
          "**Food.** Eel (smoked, stewed, or in pies), carp, duck, rye traded in from Maudry, and a sour beer brewed from marsh barley.",
          "**Trade.** Smoked eel and peat go to the Field. River pearls go to Maudry silversmiths (*Pearl Amulet*). Jellyfish are rendered for various products in autumn (*Jelly Armor*). The Oss imports iron tools from the Vey and grain from Maudry.",
          "**Law.** Towns are self-governing. When the river moves a boundary, the towns concerned must settle it themselves.",
          {
            text: "**Internal factions:**",
            items: [
              "**Dike boards** want money spent on dikes.",
              "**Pearl syndicates** want it spent on filing claims to newly exposed pearl beds.",
              "**Ferry families** hold hereditary crossing rights and oppose new causeways.",
              "The three groups sit on the same town councils.",
            ],
          },
          "**Current dispute.** The channel moved east two springs ago. Harl and Emmet's Ferry both claim the new mud flats between them. The case is on the docket for next season.",
          {
            text: "**Three eras:**",
            ordered: true,
            items: [
              "Stilt foundations of drowned villages, visible at low water.",
              "Early Tournament-era boundary stones, some now standing in the channel.",
              "New causeways built with guild loans.",
            ],
          },
          {
            text: "**Named places.**",
            items: [
              "**Harl:** mid-sized, on the eastern channel. It smokes eel for the Field.",
              "**Emmet's Ferry:** smaller, older, and dominated by two ferry families.",
              "**The Wend:** the channel's old course, now a long shallow lake, fished for carp.",
            ],
          },
          "**Eel smoking.** Eels are caught in autumn weirs, gutted, salted overnight, and hung in peat smoke for three days.",
          "**Jellyfish.** In autumn the large jellyfish wash into the lower channels. They are raked out, rendered in open vats into glue, a waterproofing paste, and pressed sheets used for padding and armor.",
        ],
      },
    ],
  },
  {
    id: "vey",
    title: "The Vey",
    summary: "Iron uplands of pit towns, forge towns, and baresteel smiths.",
    blocks: [
      {
        type: "list",
        items: [
          "**Setting.** Limestone uplands over iron ore. The Vey sold weapons to both sides during the six battles.",
          "**Daily life.** Pit towns around the mines, forge towns along the rivers, and charcoal burners in the upland woods. Miners work in shifts called “turns.” Most families have someone underground.",
          "**Food.** Oat bread, bacon, cabbage, and hard cheese. Beer is drunk at every meal, weaker in the morning.",
          "**Trade.** Ore, pig iron, finished arms and armor, tools, and precision workshop pieces (*Machinist's Crossbow*, *Scrap Launcher*). Much of the Field's equipment store was made here.",
          "**Baresteel.** Vey smiths traditionally leave their steel unpolished and unmarked (*Baresteel Aegis*). Buyers identify the maker by the hammer pattern.",
          "**Law.** The Guild of Smiths governs the forge towns, and the mine companies govern the pit towns. The two keep separate courts.",
          {
            text: "**Internal factions:**",
            items: [
              "**The Guild of Smiths** want cheap ore.",
              "**The mine companies** want higher ore prices.",
              "**Debt crews** want their terms counted in turns worked, not years served.",
              "**Charcoal burners** sell to both, and are paid at the end of each quarter.",
            ],
          },
          {
            text: "**Three eras:**",
            ordered: true,
            items: [
              "Roofless pre-Tournament foundries, now used as sheep pens.",
              "Guild halls from the first century of the Tournament.",
              "New headframes over the reopened debt mines.",
            ],
          },
          {
            text: "**Named places.**",
            items: [
              "**Carrow:** the largest forge town and seat of the Guild of Smiths, with a weekly iron market and the Vey's main money-changers.",
              "**Lathe Pit:** the largest pit town, with four headframes and a company store.",
            ],
          },
          "**Turns.** A turn is eight hours. A bell at each headframe marks the change. Miners are paid per turn, weekly.",
          "**Smithing.** A forge town is organized by street: nail makers, blade smiths, armorers, locksmiths. Apprenticeship is seven years. Baresteel smiths hammer in a personal rhythm that shows on the metal. Brokers in Carrow read the patterns and charge buyers for identification.",
        ],
      },
    ],
  },
  {
    id: "maudry",
    title: "Maudry",
    summary: "Temple-owned grain country, troubled by blight and refused tithes.",
    blocks: [
      {
        type: "list",
        items: [
          "**Setting.** Flat grain country. The temples own most of the granaries and a large share of the land.",
          "**Daily life.** Village life follows the agricultural year. There are hiring fairs each spring and autumn, where laborers stand in lines holding a tool that shows their trade. Most villages have a temple, a threshing floor and a tithe barn.",
          "**Food.** Rye bread, barley, peas, onions, pork at festivals, and small beer. In a bad year, bread is cut with pea flour.",
          "**Trade.** Grain to the Field and the Oss, silverwork (*Reliquary Pendant*), wool, and penitential goods for pilgrims.",
          "**Law.** Temple courts handle land and tithe. Village headmen handle the rest. Famine is declared by the temples, and a declaration opens the granaries at a fixed price.",
          "**Blight.** Rot has appeared in several districts for three years running. The temples signal it as a bad omen. Most know it as fungus spread by wet storage and farmers blame new drainage ditches dug beside the inspection towers.",
          {
            text: "**Internal factions:**",
            items: [
              "**The granary temples** want high prices for surplus.",
              "**Lay farmers' associations** want the famine price declared earlier.",
              "**The penitential orders** (*Masochist's Crown*) are a separate branch of the temples and often side with the farmers.",
            ],
          },
          "**Current dispute.** Farmers in the eastern districts refused the tithe last autumn.",
          {
            text: "**Three eras:**",
            ordered: true,
            items: [
              "Circular threshing floors older than any temple.",
              { value: 3, text: "Inspection towers built in the last forty years." },
            ],
          },
        ],
      },
    ],
  },
  {
    id: "hask",
    title: "Hask",
    summary: "Cold northern steppe of herders, wrestlers, and frozen ground.",
    blocks: [
      {
        type: "list",
        items: [
          "**Setting.** Cold steppe north of Maudry. The ground is frozen most of the year.",
          "**Daily life.** Herding sheep, horses and a few cattle, following summer and winter grazing grounds. Families live in felt tents in summer and turf houses in winter. Wrestling settles most arguments inside a clan.",
          "**Food.** Mutton, milk, cheese, and a fermented mare's milk drink. Grain is traded in and is expensive.",
          "**Trade.** Fur (*Bear Paws*), felt, horses, and salt from the northern pans. Hask also supplies wrestlers and, in bad years, hostages to the champion houses (*Iron Palm*).",
          "**Salt.** The northern pans are worked in summer. Salt is packed into sacks and carried south on carts, and the salt road runs through Maudry to the Field. Salt carters travel in convoys with hired escorts.",
          "**Wrestling.** Matches are held at every gathering. Wrestlers bind their hands in rawhide. A clan's best wrestlers are known by name across the steppe.",
          "**The dead.** The ground can't be dug in winter. Bodies are kept in turf ice-houses until the thaw and buried on the first dry day. Each body is marked with a notched stick.",
          "**Law.** Clan councils, elders, and wrestling.",
          {
            text: "**Internal factions:**",
            items: [
              "Northern clans refuse to be judged in an arena they didn't build.",
            ],
          },
          {
            text: "**Three eras:**",
            ordered: true,
            items: [
              "Stone cairns on the grazing routes.",
              "Abandoned signatory forts from early summer campaigns, now used as winter pens.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "gorramDesert",
    title: "The Gorram Desert",
    summary: "Shifting dunes, water drawn from the night air, and steelglass.",
    blocks: [
      {
        type: "list",
        items: [
          "One region of the larger world, home to the Wardens, the Samren, and the Steelglass Raiders.",
          {
            text: "**Daily life.**",
            items: [
              "In Warden cities, households maintain a share of the water nets and are fined if their mesh is damaged.",
              "Raider bands move with the dunes and camp in dune caves in the hottest months.",
              "The Samren live in small numbers at the few places where bedrock comes near the surface.",
            ],
          },
          "**Food.** Warden cities grow dates and millet under shade cloth irrigated from night water. Raiders eat what they trade for, plus lizards and cactus fruit. Water is rationed everywhere.",
          "**Trade.** Steelglass, and sand glass sold raw to glassworkers outside the desert. Warden hardening compounds are not sold. Instruments that predict rain are valued (*Humming Circlet*).",
          {
            text: "**Three eras:**",
            ordered: true,
            items: [
              "Samren standing stones at bedrock outcrops.",
              "Pre-Warden wells, dry.",
              "Warden cities with hardened sand walls and net towers.",
            ],
          },
          "**Hardening in practice.** Sand is wetted, mixed with the compound, and tamped into wooden forms in layers. It is left to cure for a month. Walls are recoated every few years. Workers who mix the compound wear cloth over their mouths, and the council supplies the cloth.",
          "**Shade farming.** Dates and millet are grown under woven shade cloth on terraces watered by clay pipe. Cloth is re-woven each spring from palm fiber.",
          "**Raider camps.** Camps are laid out the same way each time, in an arc facing away from the wind. Dune caves are used in the hottest months, and metal is mined there by hand. Glass is harvested after rain by sledging it off the flats before the sand moves again.",
          "**Samren listening places.** A handful of bedrock outcrops, each with standing stones and worn hand-marks on the rock.",
          "**Iscar quarter.** Temmet has a small quarter of Iscar rod-setters.",
          "**Trade routes out.** Raw sand glass goes by camel north through Sarra and the Ister passes, then on to Lenn, where it is sold to glassworkers.",
          "**Friction.** Warden roads make travel easier for caravans and harder for Raider bands, whose routes they cut across.",
        ],
      },
    ],
  },
  {
    id: "wardens",
    title: "The Wardens",
    summary:
      "City builders of Gorram who harden sand and draw water from the night air.",
    blocks: [
      {
        type: "list",
        items: [
          "Spread throughout Gorram, with bustling cities. They solidify and strengthen sand to build homes, and they extract water from the night air.",
          "**Arts.** Strength.",
          "**Government.** Each city is governed by a council of net-masters, one per tower. Temmet is the largest city.",
          "**Water.** Fine mesh strung between towers collects moisture overnight and is harvested before sunrise. Each household is assigned a length of mesh to maintain. The water ration is set daily by the council.",
          "**Hardening.** A compound mixed into wetted sand and tamped, which dries to something like fired clay. The recipe is kept by the councils.",
          "**Internal dispute.** Older towers want the mesh quota enforced strictly. Newer districts want more towers built before the quota is raised.",
          "**Pressure if unopposed.** They extend hardened roads into the dune routes the Raiders use.",
          "**Exports.** *Warden Greaves* and *Warden's Targe* are Warden-made goods sold outside the desert.",
        ],
      },
    ],
  },
  {
    id: "samren",
    title: "The Samren",
    summary: "The last few of Gorram who speak to one another through the rock.",
    blocks: [
      {
        type: "list",
        items: [
          "Only a few dozen members remain. They communicate across the desert through the rock and earth beneath them. Their remaining elders have imbued artifacts with earth-controlling abilities.",
          "**Practice.** They communicate by placing both palms flat on bedrock. This only works where rock comes near the surface, so there are a few fixed places where they can reach one another.",
          "**Names.** Samren take the name of the place where they were born.",
          "**Artifacts.** Several have left the desert over the past century.",
          "**Internal dispute.** Some elders want to recover the artifacts. Others want to teach outsiders before the last Samren die.",
        ],
      },
    ],
  },
  {
    id: "steelglassRaiders",
    title: "The Steelglass Raiders",
    summary:
      "Dune-reading bandits of Gorram who meld sand glass and metal into steelglass.",
    blocks: [
      {
        type: "list",
        items: [
          "A wandering group of bandits, many broken off from the Wardens. They have studied the movement of the dunes for hundreds of years and harvest the sand glass that appears after rare rainstorms. They meld it with metal mined in dune caves into Steelglass, which is extremely durable yet transparent.",
          "**Arts.** Strength and Dexterity.",
          "**Records.** Dune records are kept by families as knotted cords, one knot pattern per season.",
          "**Economy.** Raw sand glass is sold to glassworkers outside the desert. Steelglass is mostly kept for their own use. Water is bought or taken from Warden nets.",
          "**Internal dispute.** Older families want to keep moving with the dunes. Younger members want to settle near the dune caves and sell Steelglass openly.",
        ],
      },
    ],
  },
  {
    id: "kellDowns",
    title: "The Kell Downs",
    summary: "Chalk sheep country west of the Field, and the drove roads east.",
    blocks: [
      {
        type: "list",
        items: [
          "**Setting.** Chalk hills west of the Field. Sheep country, supplying the Field's mutton and much of Maudry's wool.",
          "**Daily life.** Shepherding, lambing in early spring, shearing in early summer, and droving to the Field in autumn. Villages sit in the valleys, and shepherds live in huts on the tops from lambing to shearing.",
          "**Food.** Mutton, sheep's cheese, barley bread, and watercress from the valley streams.",
          "**Trade.** Live sheep and mutton to the Field, fleeces to Maudry and the Field's tanneries, and tallow.",
          "**Droving.** Drovers take flocks east to the Drove Yards along fixed routes, walking about ten miles a day. Villages along the way charge for overnight grazing. Drovers carry cash for the return journey, so they travel in groups and hire escorts on the last stretch.",
          "**Law.** Village moots settle grazing disputes. Common grazing on the tops is divided each spring by lot.",
          {
            text: "**Eras.**",
            ordered: true,
            items: [
              "Chalk figures cut into the hillsides, recut every few years by the villages below.",
              "Old fort on Kell Top, now a sheepfold.",
              "Stone droving inns built along the routes in the last century.",
            ],
          },
        ],
      },
    ],
  },
];
